/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { get } from 'js/lib/user';

import { Avatar, Tag } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';
import { track } from '@coursera/event-pulse/sdk';

import { MOBILE_MAIN_IDENTIFIER } from 'bundles/composable-page-header/constants';
import { useMobileToSubPage } from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import { previewShouldShowPostPurchaseChanges } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  hasCourseraPlus?: boolean;
  noBottomBorder?: boolean;
};

const styles = {
  fullName: css`
    margin-left: var(--cds-spacing-100);
    font-weight: var(--cds-font-weight-600);
    color: var(--cds-color-neutral-primary);
    max-height: calc(3 * var(--cds-line-height-200));
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    align-self: center;
    line-height: var(--cds-line-height-250);
    text-align: left;
  `,
  nameAndPicture: css`
    display: flex;
    flex-direction: row;
  `,
  navButton: css`
    width: 100%;
    background-color: transparent;
    border: none;
    text-align: left;
    position: relative;
    padding: var(--cds-spacing-100);
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: black;
    margin-bottom: var(--cds-spacing-100);
  `,
  bottomBorder: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  avatar: css`
    &.cds-Avatar-base {
      overflow: visible;
    }
  `,
  avatarInExperiment: css`
    &.cds-Avatar-showBorder {
      border: 2px solid var(--cds-color-interactive-primary);
    }
  `,
  arrow: css`
    flex-shrink: 0;
  `,
  plusBadge: css`
    padding: 0 6px;
    position: relative;
    top: -10px;
    height: 10px;

    span {
      font-size: 9px;
    }

    &.cds-tag-status.cds-tag-primary.cds-tag-default {
      background-color: var(--cds-color-interactive-primary);
      border-color: var(--cds-color-interactive-primary);
    }
  `,
  border: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: -10px;
  `,
};

const AccountMenuItem = ({ hasCourseraPlus, noBottomBorder }: Props) => {
  const { fullName } = get();
  const photoUrl = get().photo_120;
  const toSubPage = useMobileToSubPage();
  const showCPlus = previewShouldShowPostPurchaseChanges();
  const inCPlusPromoExperiment = hasCourseraPlus && showCPlus;

  return (
    <TrackedButton
      onClick={() => {
        track('open_navigation_menu', {
          navigationType: 'profile',
        });
        toSubPage?.(MOBILE_MAIN_IDENTIFIER.ACCOUNT_MENU);
      }}
      css={[styles.navButton, noBottomBorder ? undefined : styles.bottomBorder]}
      trackingName="mobile_header_account_button"
      aria-label={_t('Go to account navigation slide')}
      tabIndex={0}
    >
      <div css={styles.nameAndPicture}>
        <div css={inCPlusPromoExperiment && styles.border}>
          <Avatar
            variant="user"
            data-testid="user-avatar"
            initial={fullName?.[0]}
            imgProps={{ src: photoUrl, alt: _t('Profile image for #{learnerName}', { learnerName: fullName }) }}
            css={[styles.avatar, inCPlusPromoExperiment && styles.avatarInExperiment]}
            showBorder={inCPlusPromoExperiment}
          />
          {inCPlusPromoExperiment && (
            <Tag variant="status" priority="primary" css={styles.plusBadge}>
              PLUS
            </Tag>
          )}
        </div>
        <span css={styles.fullName}>{fullName}</span>
      </div>
      <ChevronNextIcon css={styles.arrow} color="default" size="medium" />
    </TrackedButton>
  );
};

export default AccountMenuItem;
